/**
 * NewProcessDialog (editable-workspace-processes, PR3) — create a process board.
 *
 * label (the key is auto-slugged, shown read-only) + visibility (Everyone /
 * Restricted + a role multi-select from the six internal roles). On create the
 * caller lands on the new (empty) board in edit mode with a prompt to add the
 * first stage. Gated by the caller on canManageWorkspaceProcesses.
 */

import { useState, type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import type { WorkProcessVisibility } from '@/lib/api';
import type { UserTenantRole } from '@/lib/types';
import { ROLE_DISPLAY } from '@/lib/teamRoles';

/** The six INTERNAL roles a restricted board can be scoped to (mso_staff is
 *  cross-tenant; board_observer is the read-only board seat — neither is an
 *  internal operating role).
 *
 *  COLLAPSED 2026-08-27 onto ROLE_DISPLAY. This was the FIFTH role-label
 *  source in the tree, and it had invented a sixth set of names on top of
 *  the four that already disagreed: 'Client services' for data_steward,
 *  'Bookkeeping' for exec_support, 'Office / admin' for admin_support.
 *  Nobody scoping a board could tell that "Bookkeeping" here and "Exec
 *  support" on the Team tab were the same seat.
 *
 *  Only `value` is ever persisted (the role key, which the founder ruling
 *  deliberately left untouched); the label is display-only, and the
 *  slugPreview below derives its key from the PROCESS name the user types,
 *  never from these. */
export const INTERNAL_ROLE_OPTIONS: ReadonlyArray<{ value: UserTenantRole; label: string }> = [
  'owner',
  'data_steward',
  'exec_support',
  'supervising_cm',
  'admin_support',
  'primary_cm',
].map((value) => ({ value: value as UserTenantRole, label: ROLE_DISPLAY[value as UserTenantRole] }));

/** Mirror the server's 0154 slug rule for the read-only key preview (the server
 *  derives the authoritative key + de-dups). */
function slugPreview(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+/, '').replace(/_+$/, '');
}

export interface NewProcessDialogProps {
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onCreate: (input: { label: string; visibility: WorkProcessVisibility; allowed_roles: string[] }) => void;
}

export function NewProcessDialog({ submitting = false, error, onCancel, onCreate }: NewProcessDialogProps): ReactNode {
  const [label, setLabel] = useState('');
  const [visibility, setVisibility] = useState<WorkProcessVisibility>('all_staff');
  const [roles, setRoles] = useState<Set<string>>(new Set());

  const key = slugPreview(label);
  const restricted = visibility === 'restricted';
  // A restricted board needs at least one role, else nobody but owner/author sees it.
  const canSubmit = label.trim().length > 0 && key.length > 0 && (!restricted || roles.size > 0);

  const toggleRole = (r: string): void =>
    setRoles((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });

  return (
    <Dialog title="New process board" testId="new-process-dialog" onClose={onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onCreate({ label: label.trim(), visibility, allowed_roles: restricted ? [...roles] : [] });
        }}
      >
        <label className="block text-sm text-ink mb-1">
          Name
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            data-testid="new-process-label"
            placeholder="e.g. Hiring"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm"
          />
        </label>
        {/* The key is auto-derived + read-only (renames never change it). */}
        {key && (
          <p className="text-[11.5px] text-sage-400 mb-3" data-testid="new-process-key">
            Key: <span className="font-mono text-ink-soft">{key}</span>
          </p>
        )}

        <fieldset className="mb-3">
          <legend className="text-sm text-ink mb-1.5">Who can see this board</legend>
          <label className="flex items-center gap-2 text-sm text-ink mb-1">
            <input
              type="radio"
              name="visibility"
              checked={!restricted}
              onChange={() => setVisibility('all_staff')}
              data-testid="new-process-visibility-all"
            />
            Everyone
          </label>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="radio"
              name="visibility"
              checked={restricted}
              onChange={() => setVisibility('restricted')}
              data-testid="new-process-visibility-restricted"
            />
            Only certain roles
          </label>
        </fieldset>

        {restricted && (
          <fieldset className="mb-4 pl-1" data-testid="new-process-roles">
            <legend className="sr-only">Roles</legend>
            <div className="space-y-1">
              {INTERNAL_ROLE_OPTIONS.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={roles.has(r.value)}
                    onChange={() => toggleRole(r.value)}
                    data-testid={`new-process-role-${r.value}`}
                  />
                  {r.label}
                </label>
              ))}
            </div>
            {roles.size === 0 && (
              <p className="text-xs text-pepper mt-1.5" data-testid="new-process-role-hint">
                Pick at least one role (owners always keep access).
              </p>
            )}
          </fieldset>
        )}

        {error && <p className="text-xs text-pepper mb-3" role="alert" data-testid="new-process-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <TextAction onClick={onCancel}>Cancel</TextAction>
          <Button size="sm" type="submit" disabled={submitting || !canSubmit} data-testid="new-process-submit">
            {submitting ? 'Creating…' : 'Create board'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
